/**
 * sessionWebsocket.ts — WebSocket wrapper for the session orchestrator.
 *
 * Wraps react-use-websocket with:
 * - Fresh Cognito id_token fetched on each connect (tokens may rotate)
 * - Exponential backoff reconnect (1s → 30s cap, 10 attempts)
 * - Auto-sends {action:'resume', sessionId} frame on reconnect
 * - Token refresh on close code 4401 (server signals JWT expired)
 *
 * Security (T-02-54): getSocketUrl fetches fresh token on every (re)connect.
 * Security (T-02-55): URL is in WebSocket upgrade only; not navigated to.
 *
 * Usage:
 *   const { send, lastJsonMessage, readyState, reconnect } =
 *     useSessionSocket({ wsUrl, sessionId, getIdToken, enabled });
 */

import { useCallback, useRef } from 'react';
import _useWebSocket, { ReadyState } from 'react-use-websocket';
// Vite 8 (Rolldown) wraps CJS-only packages: the default import resolves to the whole
// exports object instead of exports.default. Unwrap to the actual hook function.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useWebSocket = (typeof (_useWebSocket as any) === 'function' ? _useWebSocket : (_useWebSocket as any).default) as typeof _useWebSocket;

export interface UseSessionSocketOptions {
  /** WebSocket base URL (VITE_WSS_URL — wss://...) */
  wsUrl: string;
  /** Current session ID (used in resume frame on reconnect) */
  sessionId: string | null;
  /** Async function that returns the current Cognito id_token */
  getIdToken: () => Promise<string>;
  /** When false, the socket is closed/not opened */
  enabled: boolean;
}

export interface UseSessionSocketReturn {
  /** Send a raw string frame */
  send: (data: string) => void;
  /** Most recent parsed JSON message from the server */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lastJsonMessage: any;
  /** react-use-websocket ReadyState enum value */
  readyState: ReadyState;
  /** Imperatively trigger a reconnect */
  reconnect: () => void;
}

export function useSessionSocket(opts: UseSessionSocketOptions): UseSessionSocketReturn {
  const { wsUrl, sessionId, getIdToken, enabled } = opts;

  // Track whether this is a reconnect (not the first connect)
  const connectCountRef = useRef(0);

  // getSocketUrl is called by react-use-websocket on each connect/reconnect.
  // Fetching a fresh token here satisfies T-02-54 (token rotation).
  const getSocketUrl = useCallback(async (): Promise<string> => {
    const token = await getIdToken();
    return `${wsUrl}?token=${encodeURIComponent(token)}`;
  }, [wsUrl, getIdToken]);

  const { sendMessage, lastJsonMessage, readyState, getWebSocket } = useWebSocket(
    enabled ? getSocketUrl : null,
    {
      // Auto-reconnect with exponential backoff
      shouldReconnect: (_closeEvent) => true,
      reconnectInterval: (attempt) => Math.min(1000 * Math.pow(2, attempt), 30000),
      reconnectAttempts: 10,

      onOpen: (_event) => {
        const isReconnect = connectCountRef.current > 0;
        connectCountRef.current += 1;

        // On reconnect (not first connect), send resume frame to restore server state
        if (isReconnect && sessionId) {
          sendMessage(JSON.stringify({ action: 'resume', sessionId }));
        }
      },

      onClose: (event) => {
        // Code 4401 means the server rejected the JWT (T-02-54)
        // react-use-websocket will call shouldReconnect + getSocketUrl on retry,
        // which fetches a fresh token automatically — no extra handling needed here.
        if (event.code === 4401) {
          console.warn('[sessionWebsocket] JWT rejected by server (4401) — will reconnect with fresh token');
        }
      },
    }
  );

  const reconnect = useCallback(() => {
    // Force a reconnect by closing the current WebSocket (react-use-websocket will reopen)
    const ws = getWebSocket();
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.close();
    }
  }, [getWebSocket]);

  return {
    send: sendMessage,
    lastJsonMessage,
    readyState,
    reconnect,
  };
}

export { ReadyState };
